import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, X as XIcon } from "lucide-react";
import PricingCard from "@/components/site/PricingCard";
import SEO from "@/components/site/SEO";
import { singleListingPlans, recurringPlans, addOns } from "@/data/pricing";
import { faqItems } from "@/data/faq";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { serviceSchema, breadcrumbSchema } from "@/data/site";

const pricingFaqSlugs = new Set([
  "Why are final prices quoted?",
  "Why not show fixed prices for everything?",
  "Can individual agents still afford LotWise Home?",
  "Do you offer postpaid plans?",
  "When is final payment due?",
  "How do monthly plans work?",
  "Are AI walkthroughs included?",
  "Do you offer video walkthroughs?",
  "Do you offer drone services?",
  "Are drone photos, video, floor plans, or virtual staging included?",
  "What support level is right for me?",
]);

export default function Pricing() {
  const pricingFaq = faqItems.filter((f) => pricingFaqSlugs.has(f.q));

  return (
    <div className="bg-white text-brand-black">
      <SEO
        title="Pricing · Real Estate Listing Support Packages"
        description="LotWise Home pricing for real estate listing photography, property detail capture, MLS-ready materials, monthly listing support, and team plans across southeastern Ontario."
        path="/pricing"
        schema={[serviceSchema, breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }])]}
      />
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-32">
          <div className="eyebrow text-white/60">Pricing</div>
          <h1 className="mt-4 text-[40px] md:text-7xl font-bold tracking-[-0.02em] leading-[1.0] max-w-4xl">
            Choose the support level that fits your listing flow.
          </h1>
          <p className="mt-8 max-w-2xl text-[16px] leading-relaxed text-white/70">
            Public pricing kept clear on purpose. Pricing starts at accessible levels, and final pricing is confirmed by
            quote once we review the property, location, scope, and deliverables. Start with one listing, build a
            repeatable monthly rhythm, or create a team-wide listing system.
          </p>
        </div>
      </section>

      {/* Quote-led explainer */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 pt-16 md:pt-20">
          <div className="border border-brand-gray/25 bg-[#F7F7F5] p-6 md:p-10">
            <div className="eyebrow-accent">How pricing works</div>
            <h2 className="mt-2 text-2xl md:text-4xl font-bold tracking-tight">Pricing starts here. Final quotes are built around the listing.</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-brand-gray max-w-3xl">
              Every listing is different. A downtown condo, a rural acreage, a family home, a luxury property, and a
              brokerage-wide listing system should not be priced the same way. LotWise Home provides starting points and
              typical ranges so agents can understand the investment level before reaching out. Final pricing is confirmed
              by quote after reviewing the property, location, timeline, and required deliverables. This keeps the service
              fair, flexible, and properly matched to the listing.
            </p>
          </div>
        </div>
      </section>

      {/* Single-listing */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="eyebrow-accent">Single Listings</div>
              <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">One property at a time.</h2>
            </div>
            <p className="text-[14px] text-brand-gray max-w-md">Listing Standard is our recommended single-listing package for most agents.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {singleListingPlans.map((p, i) => <PricingCard key={p.id} plan={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* Recurring */}
      <section className="bg-[#F7F7F5]">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-24">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="eyebrow-accent">Recurring</div>
              <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">A repeatable listing rhythm.</h2>
            </div>
            <p className="text-[14px] text-brand-gray max-w-md">Active Agent Plan is our recommended recurring option for agents with consistent listing flow.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {recurringPlans.map((p, i) => <PricingCard key={p.id} plan={p} index={i} />)}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-24">
          <div className="max-w-2xl">
            <div className="eyebrow-accent">Add-ons</div>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">Add the support your listing needs.</h2>
            <p className="mt-4 text-[15px] text-brand-gray">
              Some listings require more than the standard package. LotWise Home keeps the core service clean and affordable while making additional support available where needed.
            </p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 border-t border-brand-gray/20">
            {addOns.map((a) => (
              <div key={a.label} className="flex items-baseline justify-between border-b border-brand-gray/20 py-4">
                <span className="text-[14px] text-brand-black">{a.label}</span>
                <span className="text-[13px] text-brand-gray tabular-nums">{a.price}</span>
              </div>
            ))}
          </div>
          <p className="mt-4 text-[12.5px] text-brand-gray max-w-3xl">
            Available where applicable and quoted when needed. Subject to property type, location, and scheduling.
            Drone services are subject to weather, property location, flight conditions, and applicable rules. AI
            walkthroughs and video walkthroughs are quoted based on the required media, editing scope, and property complexity.
          </p>
        </div>
      </section>

      {/* Payment terms */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="eyebrow text-white/60">Payment terms</div>
              <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">Terms that keep scheduling and delivery controlled.</h2>
            </div>
            <ul className="lg:col-span-7 divide-y divide-white/12">
              {[
                "Smaller single-listing jobs may be paid in full at booking.",
                "Standard and larger listing jobs require 50% due at booking and 50% due before final asset delivery or go-live support.",
                "Monthly plans are paid at the start of each monthly billing period.",
                "Custom brokerage or agency plans require the first payment before work begins.",
                "Approved payment terms may be considered only for established brokerage or agency accounts.",
                "No final assets are released until the required payment has been received.",
                "Rush work, travel fees, add-ons, and larger property surcharges must be approved before the visit or before delivery.",
                "Final quote must be approved before scheduling is confirmed.",
              ].map((t) => (
                <li key={t} className="py-4 text-[14px] leading-relaxed text-white/80">{t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Service boundaries */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">What to expect</div>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight leading-tight">Included, optional, quoted, and what is not promised.</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-brand-gray">
              LotWise Home provides listing support materials, property media, media walkthroughs, and presentation
              support. Precise language matters, so here is how the scope works.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t: "Included in scope", items: ["Property photography", "Room and feature capture", "MLS-ready and web-ready exports", "Organized delivery folder", "Listing-readiness checklist"] },
              { t: "Optional / add-on", items: ["Website listing support", "Social image and video assets", "Feature sheet support materials", "Twilight and floor plan coordination", "Print-friendly selections"] },
              { t: "Quoted work", items: ["Drone exterior capture where available", "Video walkthroughs by quote", "AI walkthroughs by quote", "Rural, acreage, and larger homes", "Rush turnaround and travel"] },
              { t: "Not promised", items: ["Faster sales or higher prices", "Guaranteed showings or offers", "Guaranteed inquiries", "Guaranteed MLS placement", "Direct MLS publishing access"] },
            ].map((col) => (
              <div key={col.t} className="border border-brand-gray/25 p-5">
                <div className="eyebrow text-brand-gray">{col.t}</div>
                <ul className="mt-4 space-y-2">
                  {col.items.map((i) => (
                    <li key={i} className="text-[13.5px] leading-snug text-brand-black/85">{i}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-4">
            <div className="border-l-2 border-brand-blue/50 pl-4">
              <div className="text-[13px] font-semibold">MLS</div>
              <p className="mt-2 text-[13px] leading-relaxed text-brand-gray">
                We prepare MLS-ready materials and support agent- and brokerage-approved workflows. MLS access and
                publishing remain with the licensed professional. We do not upload, manage, or guarantee MLS placement.
              </p>
            </div>
            <div className="border-l-2 border-brand-blue/50 pl-4">
              <div className="text-[13px] font-semibold">Drone</div>
              <p className="mt-2 text-[13px] leading-relaxed text-brand-gray">
                Drone exterior capture may be available where appropriate, subject to location, weather, flight
                conditions, and applicable rules. Quoted based on the property and flight requirements.
              </p>
            </div>
            <div className="border-l-2 border-brand-blue/50 pl-4">
              <div className="text-[13px] font-semibold">Video &amp; AI walkthroughs</div>
              <p className="mt-2 text-[13px] leading-relaxed text-brand-gray">
                Video and AI walkthroughs are available by quote for select listings, priced by property size, media
                scope, editing requirements, and timeline. Designed to support a stronger online presentation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Comparison */}
      <section className="bg-[#F7F7F5]">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <div className="eyebrow-accent">Comparison</div>
            <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight leading-tight">A better listing standard without the internal drag.</h2>
          </div>

          <div className="mt-10 grid lg:grid-cols-2 gap-6">
            <div className="bg-white border border-brand-gray/25 p-6 md:p-8">
              <div className="eyebrow">DIY / Scattered</div>
              <h3 className="mt-2 text-xl font-bold">Scattered Internal Handling</h3>
              <ul className="mt-6 space-y-2">
                {["Agent coordinates everything","Assistant gathers details when available","Photography booked separately","Room and feature notes vary","Website materials lag behind","Seller follow-up increases","Rework becomes normal","Quality depends on available time","No single owner of the standard"].map((i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px] text-brand-black/70">
                    <XIcon className="h-4 w-4 mt-0.5 text-brand-gray shrink-0" /> {i}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-brand-black text-white p-6 md:p-8">
              <div className="eyebrow text-brand-blue">LotWise Home</div>
              <h3 className="mt-2 text-xl font-bold">A controlled execution layer</h3>
              <ul className="mt-6 space-y-2">
                {["Property capture handled as a system","Photos and details captured together","Room and feature documentation structured","MLS-ready and owned-channel materials prepared","Listing assets organized","Seller confidence supported","Buyer presentation stronger","The listing standard has ownership"].map((i) => (
                  <li key={i} className="flex items-start gap-2.5 text-[14px]">
                    <Check className="h-4 w-4 mt-0.5 text-brand-blue shrink-0" /> {i}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10 py-24 md:py-28">
          <div className="eyebrow-accent">Pricing FAQ</div>
          <h2 className="mt-3 text-2xl md:text-4xl font-bold tracking-tight">Answers to common pricing questions.</h2>
          <div className="mt-8">
            <Accordion type="single" collapsible data-testid="pricing-faq-accordion">
              {pricingFaq.map((f, i) => (
                <AccordionItem key={f.q} value={`p-${i}`} className="border-b border-brand-gray/25">
                  <AccordionTrigger className="text-left text-[16px] font-medium py-5 hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-[15px] leading-relaxed text-brand-gray pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-black text-white">
        <div className="mx-auto max-w-[1360px] px-5 md:px-10 py-20 md:py-28 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <h3 className="text-2xl md:text-4xl font-bold tracking-tight max-w-2xl">Ready to put a listing on the standard?</h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-brand-black px-6 py-3.5 text-[13px] font-bold">
              Request Listing Support <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link to="/faq" className="inline-flex items-center gap-2 border border-white/25 px-6 py-3.5 text-[13px] font-bold">
              Read the FAQ <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
